import { FC } from 'react';

import { HeadingTagType } from '@purpur/library';
import { match, P } from 'ts-pattern';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { withParams } from '@telialt/utils/src/helpers';
import { TeliaSegment, useSegment } from '@telialt/utils/src/segment';

import { useProductMarketingTag } from '../../hooks/useProductMarketingTag';
import { ProductSearchItem } from '../../model';
import { getFullProductUrl } from '../../utils/paths';
import { ProductCard } from '../ProductCard/ProductCard';

type Props = {
    product: ProductSearchItem;
    isCompareChecked?: boolean;
    isCompareDisabled?: boolean;
    compareDisabledMessage?: string;
    onCompare?: (checkedStatus: boolean) => void;
    onClick?: () => void;
    headingTag?: HeadingTagType;
};

export const ConnectedProductCard: FC<Props> = ({
    product,
    isCompareChecked,
    isCompareDisabled,
    compareDisabledMessage,
    onClick,
    onCompare,
    headingTag,
}) => {
    const { t } = useTranslations();
    const segment = useSegment();

    const marketingTag = useProductMarketingTag(product);

    const leasingInitialAmount = segment === TeliaSegment.B2b ? product.leasingInitialAmountFormatted : undefined;

    const leasingCommitment = product.leasingCommitment;
    const commitment = match({ segment, leasingCommitment })
        .with({ segment: TeliaSegment.B2b }, () => product.commitment)
        .with({ leasingCommitment: P.not(P.nullish) }, () =>
            withParams(t['search.component.product_card.leasing_commitment'], { leasingCommitment }),
        )
        .otherwise(() => undefined);

    const planCommitment = segment === TeliaSegment.B2c ? product.planCommitment : undefined;

    return (
        <ProductCard
            key={product.id}
            name={product.name}
            image={product.img600x}
            imageAlt={product.imgAltText}
            url={getFullProductUrl(product.url, product.variantCode)}
            colorVariants={product.colorVariants}
            monthlyPrice={product.monthlyPriceFormatted}
            oldFullPrice={product.oldFullPriceFormatted}
            fullPrice={product.fullPriceFormatted}
            residualValue={product.residualValueFormatted}
            leasingInitialAmount={leasingInitialAmount}
            commitment={commitment}
            planCommitment={planCommitment}
            badges={product.badges}
            marketingTag={marketingTag}
            marketingTagAltText={product.marketingTagAltText}
            stockStatus={product.stockStatus}
            benefits={product.benefits}
            onClick={onClick}
            isCompareChecked={isCompareChecked}
            isCompareDisabled={isCompareDisabled}
            compareDisabledMessage={compareDisabledMessage}
            onCompare={onCompare}
            headingTag={headingTag}
        />
    );
};
